import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import { getPaymentStatus } from '../services/paymentService';
import { Helmet } from 'react-helmet-async';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  padding: 2rem;
  text-align: center;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 3rem 2rem;
  max-width: 600px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h1`
  font-family: 'Press Start 2P', cursive;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
`;

const Message = styled.p`
  color: ${({ theme }) => theme.colors.text};
  margin: 1rem 0;
  line-height: 1.6;
  font-size: 1.1rem;
`;

const StatusIcon = styled.div`
  font-size: 5rem;
  margin-bottom: 2rem;
  
  &.success {
    color: #4caf50;
  }
  
  &.error {
    color: #f44336;
  }
  
  &.loading {
    color: ${({ theme }) => theme.colors.primary};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Button = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  padding: 0.8rem 2rem;
  border-radius: 5px;
  font-family: 'Press Start 2P', cursive;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 2rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  
  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const PaymentCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Verifying your payment...');
  const [error, setError] = useState(null);
  const baseUrl = process.env.REACT_APP_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const transactionId = searchParams.get('transaction_id');
        const status = searchParams.get('status');
        
        if (!transactionId) {
          throw new Error('No transaction ID found');
        }

        // In a real app, you would verify the payment status with your backend
        // const result = await getPaymentStatus(transactionId);
        
        // For demo purposes, we'll simulate a successful verification
        setTimeout(() => {
          if (status === 'success') {
            setStatus('success');
            setMessage('Payment successful! Your entry has been recorded.');
          } else {
            setStatus('error');
            setMessage('Payment failed or was cancelled.');
          }
        }, 2000);
        
      } catch (err) {
        console.error('Error verifying payment:', err);
        setStatus('error');
        setMessage('There was an error processing your payment.');
        setError(err.message);
      }
    };

    verifyPayment();
  }, [searchParams]);

  const renderStatusIcon = () => {
    switch (status) {
      case 'success':
        return <FaCheckCircle className="success" />;
      case 'error':
        return <FaTimesCircle className="error" />;
      default:
        return <FaSpinner className="loading" />;
    }
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <Container>
      <Helmet>
        <title>Payment Status — YooWin</title>
        <meta name="robots" content="noindex,nofollow" />
        <meta name="description" content="Payment verification status for your YooWin entry." />
        <link rel="canonical" href={`${baseUrl}/payment-callback`} />
        <meta property="og:title" content="Payment Status — YooWin" />
        <meta property="og:description" content="Payment verification status for your YooWin entry." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}/payment-callback`} />
      </Helmet>
      <Card>
        <StatusIcon>
          {renderStatusIcon()}
        </StatusIcon>
        <Title>
          {status === 'success' ? 'Payment Successful!' : 
           status === 'error' ? 'Payment Error' : 'Processing...'}
        </Title>
        
        <Message>{message}</Message>
        
        {error && (
          <Message style={{ color: '#f44336', fontSize: '0.9rem' }}>
            Error: {error}
          </Message>
        )}
        
        <Button onClick={handleBackToHome}>
          Back to Home
        </Button>
      </Card>
    </Container>
  );
};

export default PaymentCallbackPage;
